import { Component, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';

import { LeaveUsage, WidgetRepresentation } from './dashboard-widget.model';
import { CardHeaderComponent } from './card-header.component';
import { RepSwitcherComponent } from './rep-switcher.component';

/**
 * Leave summary: one donut ring per leave type showing days used against the
 * entitlement. A ring per type reads as a set of independent value-vs-limit
 * gauges (dataviz), which is what a leave balance is — not one pie of the whole.
 *
 * Rings are drawn with a CSS conic-gradient (no chart lib), each in its own
 * categorical hue, with used/total in the centre.
 */
@Component({
  selector: 'app-leave-summary-tile',
  standalone: true,
  imports: [CardHeaderComponent, RepSwitcherComponent, RouterLink],
  template: `
    <div
      class="card flex h-full min-h-[24rem] flex-col gap-4 rounded-xl border border-surface-200/60 bg-surface-0 p-5 dark:border-surface-700/40 dark:bg-surface-900"
    >
      <app-card-header [title]="title()" [subtitle]="subtitle()" [icon]="icon()" [accent]="accent()">
        <app-rep-switcher
          header-actions
          [reps]="reps()"
          [active]="'leave-summary'"
          (repChange)="repChange.emit($event)"
        />
      </app-card-header>

      @if (loading()) {
      <div class="grid grid-cols-3 gap-4 sm:grid-cols-5">
        @for (i of [1, 2, 3, 4, 5]; track i) {
        <div class="mx-auto h-20 w-20 animate-pulse rounded-full bg-surface-100 dark:bg-surface-800"></div>
        }
      </div>
      } @else if (items().length) {
      <!-- Tapping the rings goes to Apply Leave. -->
      <a [routerLink]="link()" class="group grid grid-cols-3 gap-y-4 sm:grid-cols-5">
        @for (item of items(); track item.type; let i = $index) {
        <div class="flex flex-col items-center gap-1.5">
          <div class="relative h-20 w-20 rounded-full" [style.background]="ring(item, i)">
            <div
              class="absolute inset-[14px] flex flex-col items-center justify-center rounded-full bg-surface-0 dark:bg-surface-900"
            >
              <span class="text-sm font-bold leading-none text-text-main">{{ item.used }}</span>
              <span class="text-[10px] leading-none text-text-muted">/ {{ item.total }}</span>
            </div>
          </div>
          <span class="text-center text-xs text-text-muted">{{ item.type }}</span>
        </div>
        }
      </a>
      <!-- <a
        [routerLink]="link()"
        class="mt-auto flex items-center gap-1 self-start text-xs font-medium text-primary-600 hover:underline dark:text-primary-400"
      >
        Apply leave <i class="pi pi-arrow-right text-[0.6rem]"></i>
      </a> -->
      } @else {
      <div class="flex flex-1 items-center justify-center text-sm text-text-muted">—</div>
      }
    </div>
  `,
})
export class LeaveSummaryTileComponent {
  title = input.required<string>();
  subtitle = input<string>('');
  icon = input<string>('');
  accent = input<string>('');
  items = input<LeaveUsage[]>([]);
  loading = input<boolean>(false);
  /** Route the card taps through to (Apply Leave). */
  link = input<string | any[]>('/leave/applications');
  /** Representations offered in the header switcher (e.g. rings / bar / pie). */
  reps = input<WidgetRepresentation[]>([]);
  repChange = output<WidgetRepresentation>();

  /** Categorical hues in fixed order — one per leave type. */
  private static readonly PALETTE = ['#3b82f6', '#f59e0b', '#14b8a6', '#8b5cf6', '#ef4444', '#22c55e'];
  private static readonly TRACK = 'rgba(148, 163, 184, 0.18)';

  /** Conic-gradient fill for a ring: hue up to the used fraction, track beyond. */
  ring(item: LeaveUsage, index: number): string {
    const pct = item.total > 0 ? Math.max(0, Math.min(100, (item.used / item.total) * 100)) : 0;
    const hue = LeaveSummaryTileComponent.PALETTE[index % LeaveSummaryTileComponent.PALETTE.length];
    return `conic-gradient(${hue} ${pct}%, ${LeaveSummaryTileComponent.TRACK} ${pct}% 100%)`;
  }
}
